/**
 * i18n Type Definitions
 *
 * Type definitions for the internationalization system.
 * Includes configuration types, callback signatures, and Crowdin integration types.
 */
import type { TFunction } from "i18next";

/**
 * Language code type - ISO 639-1 two-letter codes
 * Add new languages here as they become supported
 */
export type LanguageCode =
  | "en"
  | "da"
  | "de"
  | "es"
  | "fr"
  | "ja"
  | "ko"
  | "zh"
  | "ar";

/**
 * Language configuration for display in language switcher
 */
export interface LanguageOption {
  /** ISO 639-1 language code */
  code: LanguageCode;
  /** English name of the language */
  name: string;
  /** Native name of the language (displayed in language switcher) */
  nativeName: string;
  /** Whether the language is RTL (right-to-left) */
  isRTL?: boolean;
}

/**
 * Supported languages with their display names
 * Used for language switcher UI
 */
export const SUPPORTED_LANGUAGES: Record<LanguageCode, LanguageOption> = {
  en: { code: "en", name: "English", nativeName: "English" },
  da: { code: "da", name: "Danish", nativeName: "Dansk" },
  de: { code: "de", name: "German", nativeName: "Deutsch" },
  es: { code: "es", name: "Spanish", nativeName: "Español" },
  fr: { code: "fr", name: "French", nativeName: "Français" },
  ja: { code: "ja", name: "Japanese", nativeName: "日本語" },
  ko: { code: "ko", name: "Korean", nativeName: "한국어" },
  zh: { code: "zh", name: "Chinese", nativeName: "中文" },
  ar: { code: "ar", name: "Arabic", nativeName: "العربية", isRTL: true },
};

/**
 * i18n Backend read callback signature
 * Used by custom backends (e.g., Crowdin backend)
 */
export type ReadCallback = (
  error: Error | null,
  resources: Record<string, string> | null
) => void;

/**
 * Crowdin configuration for OTA translations
 * These values come from Crowdin project settings
 */
export interface CrowdinConfig {
  /** Crowdin distribution/project name */
  distributionName: string;
  /** Crowdin distribution hash for CDN access */
  distributionHash: string;
  /** Reference/source language code (translations fetched from local for this) */
  referenceLng: LanguageCode;
}

/**
 * i18n initialization options
 */
export interface I18nInitOptions {
  /** Initial language (defaults to browser language or 'en') */
  initialLanguage?: LanguageCode;
  /** Default namespaces to load on init */
  defaultNamespaces?: string[];
  /** Crowdin config (optional, for remote translations) */
  crowdinConfig?: CrowdinConfig;
}

/**
 * Re-export TFunction from i18next for convenience
 * This is the type of the `t` function returned by useTranslation
 */
export type TranslatorFunction = TFunction;

/**
 * Storage key for persisted language preference
 */
export const LANGUAGE_STORAGE_KEY = "i18n-language";
